<template>
  <form @submit.prevent="$emit('submit')">
    <div v-if="!hideCoursePicker" class="p-4 rounded-sm bg-white">
      <FloatLabel variant="in">
        <Select
          v-model="form.user_course.course_id"
          input-id="course"
          :options="availableCourses"
          :option-label="courseOptionLabel"
          option-value="id"
          class="w-full"
        />
        <label for="course">Curs</label>
      </FloatLabel>
      <div v-if="fieldErrors('course_id').length" class="form-error mt-1">
        {{ fieldErrors('course_id')[0] }}
      </div>
    </div>
    <div v-else class="pl-4 pt-3">
      <div class="mb-4">
        <div class="text-sm text-gray-500">Curs</div>
        <div class="font-semibold">
          {{ form.user_course.course.name }}
          <span class="text-gray-500"
            >( {{ form.user_course.course.type }} )</span
          >
        </div>
      </div>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <FormDateField
        id="start_date"
        v-model="form.user_course.start_date"
        label="Data inceput"
        :errors="fieldErrors('start_date')"
      />
      <FormDateField
        id="end_date"
        v-model="form.user_course.end_date"
        label="Data sfarsit"
        :errors="fieldErrors('end_date')"
        :min-date="form.user_course.start_date"
        :invalid="endDateInvalid"
      />
      <div class="md:col-span-2 font-bold mt-2 flex flex-row items-center">
        <label class="form-label mb-0" for="rescheduled">Reprogramare</label>
        <Checkbox
          v-model="form.user_course.rescheduled"
          input-id="rescheduled"
          class="ml-2"
          binary
        />
      </div>
      <template v-if="form.user_course.rescheduled">
        <FormDateField
          id="date_reschedule_requested"
          v-model="form.user_course.date_reschedule_requested"
          label="Data solicitării"
          :errors="fieldErrors('date_reschedule_requested')"
        />
        <FloatLabel class="" variant="in">
          <Select
            v-model="form.user_course.reschedule_reason"
            input-id="reschedule_reason"
            :options="rescheduleOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            show-clear
          />
          <label for="reschedule_reason">Motiv</label>
          <div
            v-if="fieldErrors('reschedule_reason').length"
            class="form-error mt-1"
          >
            {{ fieldErrors('reschedule_reason')[0] }}
          </div>
        </FloatLabel>
        <FormDateField
          id="restart_start_date"
          v-model="form.user_course.restart_start_date"
          label="Inceput"
          :errors="fieldErrors('restart_start_date')"
        />
        <FormDateField
          id="restart_end_date"
          v-model="form.user_course.restart_end_date"
          label="Sfarsit"
          :errors="fieldErrors('restart_end_date')"
          :min-date="form.user_course.restart_start_date"
          :invalid="restartEndDateInvalid"
        />
      </template>

      <template v-if="selectedCourse?.type?.toLowerCase() === 'certification'">
        <div class="md:col-span-2 font-bold mt-2">Predare proiect</div>
        <FormDateField
          id="project_date"
          v-model="form.user_course.project_date"
          label="Data"
          :errors="fieldErrors('project_date')"
          :min-date="start_date"
          :max-date="end_date"
          :invalid="projectDateInvalid"
        />

        <div class="md:col-span-2 font-bold mt-2">Prezentare</div>
        <FormDateField
          id="presentation_date"
          v-model="form.user_course.presentation_date"
          label="Data"
          :errors="fieldErrors('presentation_date')"
          :min-date="form.user_course.project_date || start_date"
          :max-date="end_date"
          :invalid="presentationDateInvalid"
        />
        <div class="grid grid-cols-2 gap-2">
          <FormDateField
            id="presentation_start_time"
            v-model="form.user_course.presentation_start_time"
            label="Inceput"
            :errors="fieldErrors('presentation_start_time')"
            :time-only="true"
          />
          <FormDateField
            id="presentation_end_time"
            v-model="form.user_course.presentation_end_time"
            label="Sfarsit"
            :errors="fieldErrors('presentation_end_time')"
            :time-only="true"
          />
        </div>
      </template>

      <div class="md:col-span-2 font-bold mt-2">Finalizare</div>
      <FloatLabel variant="in">
        <Select
          v-model="form.user_course.finish_status"
          input-id="finish_status"
          :options="finishStatusOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          show-clear
        />
        <label for="finish_status">Status</label>
        <div v-if="fieldErrors('finish_status').length" class="form-error mt-1">
          {{ fieldErrors('finish_status')[0] }}
        </div>
      </FloatLabel>

      <template v-if="form.user_course.finish_status === 'Finalized'">
        <div class="md:col-span-2 font-bold mt-2">Documente</div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium">Diploma</label>
          <FileUpload
            mode="basic"
            accept=".pdf,.jpg,.jpeg,.png"
            :max-file-size="10000000"
            choose-label="Alege fisier"
            class="w-full"
            @select="handleDiplomaUpload"
          />
          <div v-if="existingDiploma" class="text-sm text-gray-600 mt-1">
            Fisier existent: {{ existingDiploma }}
            <button
              type="button"
              class="text-red-600 hover:text-red-800 ml-2 cursor-pointer"
              @click="removeDiploma"
            >
              Șterge
            </button>
          </div>
        </div>
        <div
          v-if="selectedCourse?.type?.toLowerCase() === 'certification'"
          class="flex flex-col gap-2"
        >
          <label class="text-sm font-medium">Foaie matricola</label>
          <FileUpload
            mode="basic"
            accept=".pdf,.jpg,.jpeg,.png"
            :max-file-size="10000000"
            choose-label="Alege fisier"
            class="w-full"
            @select="handleGradesPaperUpload"
          />
          <div v-if="existingGradesPaper" class="text-sm text-gray-600 mt-1">
            Fisier existent: {{ existingGradesPaper }}
            <button
              type="button"
              class="text-red-600 hover:text-red-800 ml-2 cursor-pointer"
              @click="removeGradesPaper"
            >
              Șterge
            </button>
          </div>
        </div>
      </template>
    </div>

    <slot />
  </form>
</template>

<script>
import FormDateField from './FormDateField.vue';
import FloatLabel from 'primevue/floatlabel';
import Checkbox from 'primevue/checkbox';
import Select from 'primevue/select';
import FileUpload from 'primevue/fileupload';
export default {
  components: { FormDateField, Select, Checkbox, FloatLabel, FileUpload },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    availableCourses: {
      type: Array,
      required: true,
    },
    hideCoursePicker: {
      type: Boolean,
      default: false,
    },
    existingDiploma: {
      type: String,
      default: null,
    },
    existingGradesPaper: {
      type: String,
      default: null,
    },
  },
  emits: [
    'update:modelValue',
    'submit',
    'update:existingDiploma',
    'update:existingGradesPaper',
  ],
  data() {
    return {
      rescheduleOptions: [
        { label: 'Amanare', value: 'Postponing' },
        { label: 'Intrerupere', value: 'Interruption' },
        { label: 'Prelungire', value: 'Extension' },
      ],
      finishStatusOptions: [
        { label: 'Finalizat', value: 'Finalized' },
        { label: 'Nepromovat', value: 'Failed' },
        { label: 'Renunțat', value: 'Quit' },
      ],
      test: null,
    };
  },
  computed: {
    selectedCourse() {
      const cid = this.form?.user_course?.course_id;
      const found = this.availableCourses.find((c) => c.id === cid);
      if (found) return found;
      // Fallback when editing and the course is included in the payload
      const courseObj = this.form?.user_course?.course;
      return courseObj || null;
    },
    form: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    start_date() {
      return this.form.user_course.rescheduled
        ? this.form.user_course.restart_start_date
        : this.form.user_course.start_date;
    },
    end_date() {
      return this.form.user_course.rescheduled
        ? this.form.user_course.restart_end_date
        : this.form.user_course.end_date;
    },
    // Invalid flags for date pickers with constraints
    endDateInvalid() {
      return this.isOutOfRange(
        this.form.user_course.end_date,
        this.form.user_course.start_date,
        null,
      );
    },
    restartEndDateInvalid() {
      return this.isOutOfRange(
        this.form.user_course.restart_end_date,
        this.form.user_course.restart_start_date,
        null,
      );
    },
    projectDateInvalid() {
      return this.isOutOfRange(
        this.form.user_course.project_date,
        this.start_date,
        this.end_date,
      );
    },
    presentationDateInvalid() {
      const min = this.form.user_course.project_date || this.start_date;
      return this.isOutOfRange(
        this.form.user_course.presentation_date,
        min,
        this.end_date,
      );
    },
  },
  watch: {
    'form.user_course.start_date'(val) {
      if (!val) return;
      if (this.form.user_course.end_date) return;

      const d = new Date(val);
      d.setDate(d.getDate() + 180);
      this.form.user_course.end_date = d;
    },
    'form.user_course.end_date'(val) {
      if (!val) return;
      if (this.form.user_course.project_date) return;

      const d = new Date(val);
      d.setDate(d.getDate() - 7);
      this.form.user_course.project_date = d;
    },
  },
  methods: {
    fieldErrors(name) {
      const errs = this.form?.errors?.[name];
      if (!errs) return [];
      // backend may send string or array; normalize to array
      return Array.isArray(errs) ? errs : [errs];
    },
    courseOptionLabel(opt) {
      if (!opt) return '';
      const name = opt.name ?? '';
      const type = opt.type ?? '';
      return type ? `${name} (${type})` : name;
    },
    // Helpers
    toDayNumber(val) {
      if (!val) return null;
      let y, m, d;
      if (val instanceof Date) {
        y = val.getFullYear();
        m = val.getMonth() + 1;
        d = val.getDate();
      } else if (typeof val === 'string') {
        // Expecting dd.mm.yyyy
        const match = val.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (!match) return null;
        d = parseInt(match[1], 10);
        m = parseInt(match[2], 10);
        y = parseInt(match[3], 10);
      } else {
        return null;
      }
      return y * 10000 + m * 100 + d;
    },
    isOutOfRange(dateVal, minVal, maxVal) {
      const dn = this.toDayNumber(dateVal);
      if (dn == null) return false; // nothing selected can't be invalid visually
      const min = this.toDayNumber(minVal);
      const max = this.toDayNumber(maxVal);
      if (min != null && dn < min) return true;
      if (max != null && dn > max) return true;
      return false;
    },
    handleDiplomaUpload(event) {
      const file = event.files[0];
      if (file) {
        this.form.user_course.diploma = file;
      }
    },
    handleGradesPaperUpload(event) {
      const file = event.files[0];
      if (file) {
        this.form.user_course.grades_paper = file;
      }
    },
    removeDiploma() {
      this.form.user_course.remove_diploma = true;
      this.form.user_course.diploma = null;
      this.$emit('update:existingDiploma', null);
    },
    removeGradesPaper() {
      this.form.user_course.remove_grades_paper = true;
      this.form.user_course.grades_paper = null;
      this.$emit('update:existingGradesPaper', null);
    },
  },
};
</script>
